import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import api from '../../services/api';
import { Button } from '../../components/Button';
import { LogOut, Plus, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { OrdemCard } from '../../components/OrdemCard';
import { OrdemDetalhes } from '../../components/OrdemDetalhes';
import { ModalImpressao } from '../../components/ModalImpressao';
import { Ordem, PedidoDetalhe } from '../../types';
import './styles.css';
import Logo from '../../assets/logo.svg';
import { MenuNovoMaterial } from './MenuNovoMaterial';
import { MenuNovoModelo } from './MenuNovoModelo';
import { MenuNovoUsuario } from './MenuNovoUsuario';
import { MenuAcoes } from './MenuAcoes';
import { MenuNovaOrdemServico } from './MenuNovaOrdemServico';

export const Dashboard: React.FC = () => {
  const { handleClearToken } = useAuth();
  const navigate = useNavigate();
  const [ordens, setOrdens] = useState<Ordem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrdem, setSelectedOrdem] = useState<Ordem | null>(null);
  const [showNovoMaterial, setShowNovoMaterial] = useState(false);
  const [showNovoModelo, setShowNovoModelo] = useState(false);
  const [showNovoUsuario, setShowNovoUsuario] = useState(false);
  const [showNovaOrdem, setShowNovaOrdem] = useState(false);
  const [showModalImpressao, setShowModalImpressao] = useState(false);
  const [ordemParaImprimir, setOrdemParaImprimir] = useState<Ordem | null>(null);
  const [pedidosParaImprimir, setPedidosParaImprimir] = useState<PedidoDetalhe[]>([]);
  const [loadingImpressao, setLoadingImpressao] = useState(false);

  useEffect(() => {
    fetchOrdens();
  }, []);

  useEffect(() => {
    // Expõe fetchOrdens globalmente para atualização cross-component
    (window as any).fetchOrdens = fetchOrdens;
    return () => {
      delete (window as any).fetchOrdens;
    };
  }, []);

  const fetchOrdens = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/ordens_servico/');
      // Mapeia os dados para o formato esperado pelo frontend
      const ordensBackend = response.data;
      const ordensMapeadas = ordensBackend.map((o: any) => ({
        id: o.id,
        data: o.data,
        pedidos: o.pedidos?.map((p: any) => ({
          id: p.id,
          modelo: typeof p.modelo === 'object' ? p.modelo.id : p.modelo, // garante id do modelo
          quantidade: p.quantidade,
          material: '', // será preenchido no detalhe
          preco_unidade: p.preco_unidade,
          preco_total_pedido: p.preco_total_pedido,
          status: p.status, // mantém string
          cor: p.cor ?? '',
          observacao: p.observacao ?? ''
        })),
        valor_total: o.total, // backend retorna como total
        status: o.status, // mantém string
        finalizado_em: o.finalizado_em ?? null
      }));
      // Filtra ordens pendentes ou em produção
      const ordensPendentes = ordensMapeadas.filter((o: any) => 
        o.status === 'pendente' || o.status === 'em_producao'
      );
      setOrdens(ordensPendentes);
    } catch (error) {
      console.error('Erro ao buscar ordens:', error);
      toast.error('Erro ao carregar ordens de serviço');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    handleClearToken();
    navigate('/');
  };

  const handleOrdemClick = (ordem: Ordem) => {
    setSelectedOrdem(ordem);
  };

  const handleCloseDetalhes = () => {
    setSelectedOrdem(null);
    fetchOrdens();
  };

  const handleOrdemUpdated = (updated: Partial<Ordem> & { id: number }) => {
    setOrdens(prev => prev.map(o => {
      if (o.id !== updated.id) return o;
      return { ...o, ...updated } as Ordem;
    }));
  };

  const handlePrintOrdem = async (ordem: Ordem) => {
    try {
      setLoadingImpressao(true);
      setOrdemParaImprimir(ordem);

      // Busca modelos e materiais para preencher os nomes
      const [resModelos, resMateriais] = await Promise.all([
        api.get('/api/v1/modelos/'),
        api.get('/api/v1/materiais/')
      ]);

      const modelos = resModelos.data;
      const materiais = resMateriais.data;

      // Converte pedidos para o formato detalhado com nomes
      const pedidosDetalhados: PedidoDetalhe[] = (ordem.pedidos || []).map((p: any) => {
        let modeloId = p.modelo ?? '';
        if (typeof modeloId === 'object' && modeloId !== null) modeloId = modeloId.id;
        
        const modelo = modelos.find((m: any) => m.id === Number(modeloId));
        const materialId = modelo?.material ? (typeof modelo.material === 'object' ? modelo.material.id : modelo.material) : null;
        const material = materialId ? materiais.find((mat: any) => mat.id === materialId) : null;

        return {
          id: p.id,
          produto: '',
          quantidade: p.quantidade,
          material: '',
          preco_inicial: p.preco_unidade ?? 0,
          preco_final: p.preco_total_pedido ?? 0,
          status: p.status as 'pendente' | 'em_producao' | 'finalizado',
          cor: p.cor ?? '',
          observacao: p.observacao ?? '',
          modelo_nome: modelo?.nome || modeloId.toString(),
          material_nome: material?.nome || '',
        };
      });

      setPedidosParaImprimir(pedidosDetalhados);
      setShowModalImpressao(true);
    } catch (error) {
      console.error('Erro ao preparar impressão:', error);
      toast.error('Erro ao carregar dados para impressão');
    } finally {
      setLoadingImpressao(false);
    }
  };

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-header-container">
          <div className="dashboard-header-content">
            <img src={Logo} alt="Logo" className="dashboard-logo" />
            <MenuAcoes onNovoMaterial={() => setShowNovoMaterial(true)}
                      onNovoModelo={() => setShowNovoModelo(true)}
                      onNovoUsuario={() => setShowNovoUsuario(true)}
            />
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="outline"
            className="dashboard-logout-button"
          >
            <LogOut className="icon-small" />
            Sair
          </Button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-main-header">
          <div className="dashboard-main-header-content">
            <h2 className="dashboard-section-title">
              Ordens de Serviço Pendentes
            </h2>
            <p className="dashboard-section-subtitle">
              {ordens.length} {ordens.length === 1 ? 'ordem pendente' : 'ordens pendentes'}
            </p>
          </div>
          <div className="dashboard-main-header-buttons">
            <Button
              data-testid="ordens-finalizadas-button"
              className="dashboard-ver-finalizadas-button"
              onClick={() => navigate('/finalizadas')}
            >
              <FileText className="icon-small" />
              Ordens Finalizadas
            </Button>
            <Button
              data-testid="add-ordem-button"
              className="dashboard-add-button"
              onClick={() => setShowNovaOrdem(true)}
            >
              <Plus className="icon-small" />
              Nova Ordem
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading">
            <div className="dashboard-spinner"></div>
          </div>
        ) : ordens.length === 0 ? (
          <div className="dashboard-empty">
            <p className="dashboard-empty-text">Nenhuma ordem de serviço registrada.</p>
          </div>
        ) : (
          <div className="dashboard-cards-fullwidth">
            {ordens.map((ordem) => (
              <OrdemCard
                key={ordem.id}
                ordem={ordem}
                onClick={() => handleOrdemClick(ordem)}
                onPrint={handlePrintOrdem}
              />
            ))}
          </div>
        )}
      </main>

      {selectedOrdem && (
        <OrdemDetalhes
          ordem={selectedOrdem}
          onClose={handleCloseDetalhes}
          onUpdated={handleOrdemUpdated}
        />
      )}

      {showNovoMaterial && (
        <MenuNovoMaterial
          onClose={() => setShowNovoMaterial(false)}
          onSuccess={fetchOrdens}
        />
      )}
      {showNovoModelo && (
        <MenuNovoModelo
          onClose={() => setShowNovoModelo(false)}
          onSuccess={fetchOrdens}
        />
      )}
      {showNovoUsuario && (
        <MenuNovoUsuario
          onClose={() => setShowNovoUsuario(false)}
          onSuccess={fetchOrdens}
        />
      )}
      {showNovaOrdem && (
        <MenuNovaOrdemServico
          onClose={() => setShowNovaOrdem(false)}
          onSuccess={fetchOrdens}
        />
      )}

      {showModalImpressao && ordemParaImprimir && (
        <ModalImpressao
          ordem={ordemParaImprimir}
          pedidos={pedidosParaImprimir}
          onClose={() => {
            setShowModalImpressao(false);
            setOrdemParaImprimir(null);
            setPedidosParaImprimir([]);
          }}
        />
      )}
    </div>
  );
};

